import { useMessagingSectionCountersQuery } from '@/hooks/queries/use-messaging-section-counters-query';
import { NAV_ICON_STROKE_WIDTH } from '@/components/icons';
import CounterBadge from '@/components/scaffold/nav-bar/CounterBadge';
import SidebarSectionLabel from '@/components/scaffold/nav-bar/components/SidebarSectionLabel';
import NavLink from '@/components/scaffold/nav-bar/nav-link';
import { Box, Flex } from '@chakra-ui/react';
import { Archive, MessageText, SendMail } from 'iconoir-react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';

type ConversationFilter = 'inbox' | 'sent' | 'archived';

const filterParser = parseAsStringLiteral(['inbox', 'sent', 'archived'] as const).withDefault(
  'inbox'
);

interface NavItemConfig {
  value: ConversationFilter;
  icon: React.ReactNode;
  labelKey: string;
  section: 'conversations' | 'email';
}

const SECTIONS = [
  { key: 'conversations', labelKey: 'messages.nav.conversations' },
  { key: 'email', labelKey: 'messages.nav.email' },
] as const;

const NAV_ITEMS: NavItemConfig[] = [
  {
    value: 'inbox',
    icon: <MessageText width={18} height={18} strokeWidth={NAV_ICON_STROKE_WIDTH} />,
    labelKey: 'messages.filter.inbox',
    section: 'conversations',
  },
  {
    value: 'archived',
    icon: <Archive width={18} height={18} strokeWidth={NAV_ICON_STROKE_WIDTH} />,
    labelKey: 'messages.filter.archived',
    section: 'conversations',
  },
  {
    value: 'sent',
    icon: <SendMail width={18} height={18} strokeWidth={NAV_ICON_STROKE_WIDTH} />,
    labelKey: 'messages.filter.sent',
    section: 'email',
  },
];

interface MessagingNavItemProps {
  item: NavItemConfig;
  isActive: boolean;
  count?: number;
  onClick: () => void;
}

/**
 * A filter row of the Inbox panel: a menu row, a touch tighter than the main
 * menu's, whose counter keeps its slot even when empty so the labels never shift.
 */
const MessagingNavItem: React.FC<MessagingNavItemProps> = ({ item, isActive, count, onClick }) => {
  const { t } = useTranslation();
  const showCount = count !== undefined && count > 0;

  return (
    <NavLink
      label={t(item.labelKey)}
      icon={item.icon}
      iconBoxSize="18px"
      isActive={isActive}
      onClick={onClick}
      h="32px"
      fontSize="14px"
      aria-current={isActive ? 'true' : undefined}
      rightChildren={
        <CounterBadge
          count={showCount ? count : 0}
          active={isActive}
          visibility={showCount ? 'visible' : 'hidden'}
          aria-hidden={!showCount}
          data-testid={`messaging-nav-counter-${item.value}`}
          data-active={isActive ? 'true' : 'false'}
          data-count-visible={showCount ? 'true' : 'false'}
        />
      }
    />
  );
};

const MessagingNav: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [urlFilter] = useQueryState('messageFilter', filterParser);
  const { data: counters } = useMessagingSectionCountersQuery(user?.currentBrandId);

  // Derive active filter: URL path takes precedence over query param
  const isOnSentDetail = location.pathname.startsWith('/messages/sent/');
  const currentFilter = isOnSentDetail ? ('sent' as const) : urlFilter;

  const handleFilterChange = (filter: ConversationFilter) => {
    navigate(`/messages?messageFilter=${filter}`);
  };

  // The inbox badge counts what is left to read, not what the section holds:
  // a total never falls back to zero, so it would sit there for good. Archives
  // and sent mail have no notion of unread and show their size.
  const getItemCount = (filter: ConversationFilter) => {
    if (!counters) return undefined;

    if (filter === 'inbox') return counters.inboxUnread;
    if (filter === 'archived') return counters.archived;
    return counters.sentEmails;
  };

  return (
    <Box as="nav" aria-label={t('messages.nav.ariaLabel')}>
      <Flex direction="column" gap={4}>
        {SECTIONS.map((section) => {
          const items = NAV_ITEMS.filter((item) => item.section === section.key);
          return (
            <Box key={section.key}>
              <SidebarSectionLabel>{t(section.labelKey)}</SidebarSectionLabel>
              <Flex direction="column" gap="1px">
                {items.map((item) => (
                  <MessagingNavItem
                    key={item.value}
                    item={item}
                    isActive={currentFilter === item.value}
                    count={getItemCount(item.value)}
                    onClick={() => handleFilterChange(item.value)}
                  />
                ))}
              </Flex>
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
};

export default MessagingNav;
